import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BellSimple, WarningOctagon, Clock, Info, Timer, Checks } from "@phosphor-icons/react";
import { toast } from "sonner";

const PRIO = {
  kritisch: { tone: "border-l-red-500", badge: "border-red-500/40 bg-red-500/10 text-red-600", icon: WarningOctagon, label: "Kritisch" },
  sla: { tone: "border-l-amber-500", badge: "border-amber-500/40 bg-amber-500/10 text-amber-600", icon: Timer, label: "SLA" },
  erinnerung: { tone: "border-l-blue-500", badge: "border-blue-500/40 bg-blue-500/10 text-blue-600", icon: Clock, label: "Erinnerung" },
  info: { tone: "border-l-slate-400", badge: "border-border bg-muted text-muted-foreground", icon: Info, label: "Info" },
};

export default function Benachrichtigungen() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("alle");
  const [urgentOnly, setUrgentOnly] = useState(false);
  const [counts, setCounts] = useState({ unread: 0, kritisch: 0, sla: 0 });
  const navigate = useNavigate();

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (!urgentOnly && filter !== "alle") p.set("priority", filter);
    api.get(`/notifications?${p.toString()}`).then((r) => {
      const data = urgentOnly ? r.data.filter((n) => n.priority === "kritisch" || n.priority === "sla") : r.data;
      setItems(data);
    });
    api.get("/notifications/unread-count").then((r) => setCounts(r.data));
  }, [filter, urgentOnly]);
  useEffect(() => { load(); }, [load]);

  const openItem = async (n) => {
    if (!n.read) { await api.post(`/notifications/${n.id}/read`); load(); }
    if (n.ref_route) navigate(n.ref_route);
  };
  const readAll = async () => {
    await api.post("/notifications/read-all");
    toast.success("Alle als gelesen markiert");
    load();
  };

  return (
    <div>
      <PageHeader title="Benachrichtigungscenter" subtitle="Zentrale Erinnerungen, SLA-Warnungen & Ereignisse (D-15)">
        <Button size="sm" variant={urgentOnly ? "default" : "outline"} className="gap-2"
          onClick={() => setUrgentOnly((v) => !v)} data-testid="notif-quickfilter-urgent">
          <WarningOctagon size={16} weight="fill" /> Nur dringend
          {(counts.kritisch + counts.sla) > 0 && (
            <span className="ml-0.5 rounded-full bg-red-500/90 px-1.5 text-[10px] font-bold text-white">{counts.kritisch + counts.sla}</span>
          )}
        </Button>
        <Badge variant="outline" data-testid="notif-unread-badge" className="gap-1.5 rounded-md border-primary/30 bg-primary/10 text-primary">
          <BellSimple size={14} weight="fill" /> {counts.unread} ungelesen
        </Badge>
        <Button size="sm" variant="outline" className="gap-2" onClick={readAll} data-testid="notif-read-all">
          <Checks size={16} weight="bold" /> Alle gelesen
        </Button>
      </PageHeader>

      <Tabs value={filter} onValueChange={setFilter} className={`mb-4 ${urgentOnly ? "pointer-events-none opacity-40" : ""}`}>
        <TabsList data-testid="notif-tabs">
          <TabsTrigger value="alle">Alle</TabsTrigger>
          <TabsTrigger value="kritisch">Kritisch</TabsTrigger>
          <TabsTrigger value="sla">SLA</TabsTrigger>
          <TabsTrigger value="erinnerung">Erinnerungen</TabsTrigger>
          <TabsTrigger value="info">Info</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="divide-y divide-border rounded-md border">
        {items.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Keine Benachrichtigungen.</div>}
        {items.map((n) => {
          const meta = PRIO[n.priority] || PRIO.info;
          const Icon = meta.icon;
          return (
            <button key={n.id} onClick={() => openItem(n)} data-testid={`notif-${n.id}`}
              className={`flex w-full items-start gap-3 border-l-4 ${meta.tone} px-4 py-3.5 text-left transition-colors hover:bg-accent ${n.read ? "opacity-60" : "bg-primary/[0.03]"}`}>
              <Icon size={20} weight="duotone" className="mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />}
                  <span className="truncate text-sm font-semibold">{n.subject}</span>
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{n.recipient}</span><span>·</span>
                  <span>{new Date(n.created_at).toLocaleString("de-DE")}</span>
                </div>
              </div>
              <Badge variant="outline" className={`shrink-0 rounded-md text-xs ${meta.badge}`}>{meta.label}</Badge>
            </button>
          );
        })}
      </Card>
    </div>
  );
}
